// src/services/metering.js
// Centralized credit metering for Narrative & Artifact flows.
// Keeps call-sites tiny and consistent across image/video/tts/screenplay/char-bible/merge.

import * as creditService from './creditService';
import { isTutorialModeActive } from './TutorialInterceptor';

// ---------- Feature flags (flip gradually) ----------
export const FEATURE_CREDITS_GLOBAL = true;   // global kill switch
export const FEATURE_CREDITS_NARRATIVE = true;   // narrative workbench metering
export const FEATURE_CREDITS_SCENE_IMAGES = true;   // per-scene image gen
export const FEATURE_CREDITS_SCENE_VIDEOS = true;  // per-scene video gen (enable later)
export const FEATURE_CREDITS_TTS = false;  // dialogue TTS metering (enable later)
export const FEATURE_CREDITS_SCREENPLAY = true;  // screenplay (LLM) metering (enable later)
export const FEATURE_CREDITS_CHAR_BIBLE = true;  // character bible (LLM) metering (enable later)
export const FEATURE_CREDITS_MERGE = false;  // video merge metering (enable later)
export const FEATURE_CREDITS_SCENES = true;

// ---------- Price codes (must exist in pricebook) ----------
export const PRICE_CODES = {
  image: 'image_1024',   // flat
  video: 'video_sec',    // per_second
  tts: 'tts_sec',      // per_second (approx by text length or actual duration)
  screenplayJob: 'screenplay_job',     // flat (seed in pricebook when enabling)
  charBibleUnit: 'char_bible_unit',    // per_unit (e.g., per character parsed)
  mergeSec: 'merge_sec',          // per_second (final movie length)
  sceneUnit: 'scene_unit',
  music: 'music_gen',
};

// ---------- Helpers ----------
const isEnabled = (...flags) => flags.every(Boolean) && FEATURE_CREDITS_GLOBAL;

const makeIdem = (...parts) =>
  parts.filter(Boolean).map(String).join('|'); // stable idempotency key

const classifyErr = (e) => {
  const code = e?.body?.error || e?.code;
  const status = e?.status;
  return {
    status,
    code,
    insufficient: status === 402 || code === 'INSUFFICIENT_FUNDS',
    unknownPrice: status === 400 && /unknown price code/i.test(String(e?.message || code || '')) ||
      status === 400 && code === 'Unknown price code',
  };
};

const makeRunNonce = () => String(Date.now());

const reserveThen = async ({ code, units, idem, metadata, run, artifactId }) => {
  let ledgerId = null;
  let idempotentCommitted = false;
  try {
    const r = await creditService.reserve(code, units, idem, metadata);
    ledgerId = r.ledgerId;
    // If backend returns an already-committed idempotent reservation, don’t refinalize.
    if (r?.idempotent && r?.status === 'committed') {
      idempotentCommitted = true;
    }
    const result = await run();
    if (!idempotentCommitted) {
      try { await creditService.finalize(ledgerId, artifactId); } catch { /* avoid noisy 400s */ }
    }
    return result;
  } catch (e) {
    if (ledgerId && !idempotentCommitted) {
      try { await creditService.revert(ledgerId, 'generation_failed'); } catch { }
    }
    throw e;
  }
};

// Debit after success (for TTS or things where cost known only after success)
const debitAfterSuccess = async ({ code, units, idem, metadata, run }) => {
  const result = await run();
  try { await creditService.debit(code, units, idem, metadata); } catch { }
  return result;
};

// Fallback wrapper: if flag disabled or unknown price, just run without billing
const safeWrap = async (enabled, worker, onErrorBypass = false) => {
  // if (!enabled) return worker(); // REMOVED: Always wrap to catch 402s and show banner
  try {
    return await worker();
  } catch (e) {
    const { unknownPrice, insufficient } = classifyErr(e);
    if (insufficient) {
      const meta = {
        required: e.body?.cost,
        balance: e.body?.balance
      };
      creditService.setInsufficientFlag(meta);
    }
    if (onErrorBypass && unknownPrice) {
      // Price code not seeded yet → bypass billing and still run job
      return await worker.__bareRun?.();
    }
    throw e;
  }
};

// ---------- Public API ----------

// Per-scene IMAGE (reserve → finalize; revert on failure)
export async function runSceneImage({ projectId, sceneNumber, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_NARRATIVE, FEATURE_CREDITS_SCENE_IMAGES);
  const code = PRICE_CODES.image;
  const units = 1;
  const idem = makeIdem(projectId, sceneNumber, 'image');

  const worker = () => reserveThen({
    code, units, idem,
    metadata: { projectId, sceneNumber, kind: 'image', ...meta },
    run: runCore,
  });

  // If price not seeded yet, bypass billing but still run
  worker.__bareRun = runCore;

  if (isTutorialModeActive) return runCore();

  return safeWrap(enabled, worker, /*onErrorBypass=*/true);
}

// Per-scene VIDEO (units = durationSec)
export async function runSceneVideo({ projectId, sceneNumber, durationSec, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_NARRATIVE, FEATURE_CREDITS_SCENE_VIDEOS);
  const code = PRICE_CODES.video;
  const units = Math.max(1, Math.ceil(Number(durationSec || 1)));
  const idem = makeIdem(projectId, sceneNumber, 'video');
  const artifactId = `${projectId}:scene:${sceneNumber}:video`;

  const worker = () => reserveThen({
    code, units, idem,
    metadata: { projectId, sceneNumber, kind: 'video', duration: units, ...meta },
    run: runCore,
    artifactId,
  });
  worker.__bareRun = runCore;

  if (isTutorialModeActive) return runCore();

  return safeWrap(enabled, worker, true);
}

// TTS per dialogue line (debit after success; estimate seconds or pass actual)
export async function runTTSLine({ projectId, sceneNumber, lineIndex, estSeconds, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_NARRATIVE, FEATURE_CREDITS_TTS);
  const code = PRICE_CODES.tts;
  const units = Math.max(1, Math.ceil(Number(estSeconds || 1)));
  const idem = makeIdem(projectId, sceneNumber, 'tts', lineIndex);

  const worker = () => debitAfterSuccess({
    code, units, idem,
    metadata: { projectId, sceneNumber, kind: 'tts', lineIndex, estSeconds: units, ...meta },
    run: runCore,
  });
  worker.__bareRun = runCore;

  if (isTutorialModeActive) return runCore();

  return safeWrap(enabled, worker, true);
}

// Screenplay (flat job)
export async function runScreenplay({ projectId, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_SCREENPLAY);
  const code = PRICE_CODES.screenplayJob;
  const units = 1;
  const idem = makeIdem(projectId, 'screenplay');

  const worker = () => reserveThen({
    code, units, idem,
    metadata: { projectId, kind: 'screenplay', ...meta },
    run: runCore,
  });
  worker.__bareRun = runCore;

  if (isTutorialModeActive) return runCore();

  return safeWrap(enabled, worker, true);
}

// Character Bible (unknown units): reserve max, then settle actual
export async function runCharacterBibleAdjusted(
  { projectId, unitsMax = 20, meta = {}, calcUnits },   // calcUnits(result) -> number
  runCore                                              // returns result (e.g., { characterMap, ... })
) {
  // gate
  if (!isEnabled(FEATURE_CREDITS_CHAR_BIBLE)) return runCore();
  if (isTutorialModeActive) return runCore();

  const code = PRICE_CODES.charBibleUnit;              // must exist in pricebook
  const idemReserve = makeIdem(projectId, 'char_bible_est', unitsMax);

  let ledgerId = null;
  try {
    // 1) Hold funds up to the safe upper bound
    const r = await creditService.reserve(
      code, unitsMax, idemReserve,
      { projectId, kind: 'character_bible', estimate: unitsMax, ...meta }
    );
    ledgerId = r.ledgerId;

    // 2) Run the actual generation
    const result = await runCore();
    const actualUnits = Math.max(
      1,
      Number(typeof calcUnits === 'function' ? await calcUnits(result) : 1)
    );

    // 3) Release the hold, then charge the exact amount
    try { await creditService.revert(ledgerId, 'adjust_to_actual'); } catch { }
    const idemDebit = makeIdem(projectId, 'char_bible_actual', actualUnits);
    await creditService.debit(
      code, actualUnits, idemDebit,
      { projectId, kind: 'character_bible', actual: actualUnits, ...meta },
      undefined, // userId (optional)
      true       // allowOverdraft
    );

    return result;
  } catch (e) {
    const { insufficient } = classifyErr(e);
    if (insufficient) {
      creditService.setInsufficientFlag({
        required: e.body?.cost,
        balance: e.body?.balance
      });
    }

    // On error, release the hold
    if (ledgerId) { try { await creditService.revert(ledgerId, 'generation_failed'); } catch { } }
    throw e;
  }
}



// Character Bible (per-unit: number of characters parsed/requested)
export async function runCharacterBible({ projectId, units = 1, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_CHAR_BIBLE);
  const code = PRICE_CODES.charBibleUnit;
  const idem = makeIdem(projectId, 'char_bible', units);

  const worker = () => reserveThen({
    code, units, idem,
    metadata: { projectId, kind: 'character_bible', units, ...meta },
    run: runCore,
  });
  worker.__bareRun = runCore;

  if (isTutorialModeActive) return runCore();

  return safeWrap(enabled, worker, true);
}


export async function runScenesAdjusted(
  { projectId, unitsMax, meta = {}, calcUnits }, runCore
) {
  const enabled = isEnabled(FEATURE_CREDITS_NARRATIVE, FEATURE_CREDITS_SCENES);
  const code = PRICE_CODES.sceneUnit;                            // <-- must exist
  const maxUnits = Math.max(1, Number(unitsMax || 1));
  const idemReserve = makeIdem(projectId, 'scenes_est', maxUnits); // <-- non-empty

  const worker = async () => {
    let ledgerId;
    try {
      const r = await creditService.reserve(code, maxUnits, idemReserve,
        { projectId, kind: 'scenes', estimate: maxUnits, ...meta });
      ledgerId = r.ledgerId;

      const result = await runCore();
      const actual = Math.max(1, Number(await (calcUnits?.(result) ?? 1)));

      try { await creditService.revert(ledgerId, 'adjust_to_actual'); } catch { }
      const idemDebit = makeIdem(projectId, 'scenes_actual', actual);
      await creditService.debit(code, actual, idemDebit,
        { projectId, kind: 'scenes', actual, ...meta });

      return result;
    } catch (e) {
      if (ledgerId) { try { await creditService.revert(ledgerId, 'generation_failed'); } catch { } }
      throw e;
    }
  };

  // If pricebook doesn’t have scene_unit yet, still run without billing
  worker.__bareRun = runCore;

  if (isTutorialModeActive) return runCore();

  return safeWrap(enabled, worker, /*onErrorBypass=*/true);
}


// Merge final movie (units = output seconds)
export async function runMerge({ projectId, outSeconds, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_MERGE);
  const code = PRICE_CODES.mergeSec;
  const units = Math.max(1, Math.ceil(Number(outSeconds || 1)));
  const idem = makeIdem(projectId, 'merge', units);

  const worker = () => reserveThen({
    code, units, idem,
    metadata: { projectId, kind: 'merge', duration: units, ...meta },
    run: runCore,
  });
  worker.__bareRun = runCore;

  if (isTutorialModeActive) return runCore();

  return safeWrap(enabled, worker, true);
}

// --- Scene video: explicit reserve/commit/cancel (partial-charge aware) ---
export async function reserveSceneVideo({ projectId, sceneNumber, maxUnits, meta = {} }) {
  const enabled = isEnabled(FEATURE_CREDITS_NARRATIVE, FEATURE_CREDITS_SCENE_VIDEOS);
  if (!enabled || isTutorialModeActive) return { reservationId: null, bypass: true };
  const code = PRICE_CODES.video;
  const units = Math.max(1, Math.ceil(Number(maxUnits || 1)));
  const idem = makeIdem(projectId, sceneNumber, 'video_batch');
  const r = await creditService.reserve(code, units, idem, {
    projectId, sceneNumber, kind: 'video', duration: units, ...meta
  });
  return { reservationId: r.ledgerId };
}

export async function commitSceneVideo(reservationId, usedUnits, meta = {}) {
  if (!reservationId) return;
  const code = PRICE_CODES.video;
  const actual = Math.max(1, Math.ceil(Number(usedUnits || 1)));
  // Adjust: release hold, then debit actual
  try { await creditService.revert(reservationId, 'adjust_to_actual'); } catch { }
  const idem = makeIdem('video_actual', reservationId, actual);
  await creditService.debit(code, actual, idem, { kind: 'video', actual, ...meta });
}

export async function cancelSceneVideo(reservationId, reason = 'failed') {
  if (!reservationId) return;
  try { await creditService.revert(reservationId, reason); } catch { }
}

// ---------- Artifact Workbench helpers (STRICT: no bypass; unique idem per run) ----------
export async function runArtifactImage({ projectId, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_GLOBAL, FEATURE_CREDITS_SCENE_IMAGES);
  const code = PRICE_CODES.image;
  const units = 1;
  const idem = makeIdem(projectId, 'artifact', 'image', makeRunNonce());
  const worker = () => reserveThen({
    code, units, idem,
    metadata: { projectId, kind: 'image', source: 'artifact-workbench', ...meta },
    run: runCore,
  });
  // Do NOT bypass on unknown price codes (surface the error).
  return safeWrap(enabled, worker, /*onErrorBypass=*/false);
}

export async function runArtifactVideo({ projectId, durationSec, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_GLOBAL, FEATURE_CREDITS_SCENE_VIDEOS);
  const code = PRICE_CODES.video;
  const units = Math.max(1, Math.ceil(Number(durationSec || 1)));
  const idem = makeIdem(projectId, 'artifact', 'video', units, makeRunNonce());
  const artifactId = `${projectId}:artifact:video:${Date.now()}`;
  const worker = () => reserveThen({
    code, units, idem,
    metadata: { projectId, kind: 'video', seconds: units, source: 'artifact-workbench', ...meta },
    run: runCore,
    artifactId,
  });
  // Do NOT bypass on unknown price codes (surface the error).
  return safeWrap(enabled, worker, /*onErrorBypass=*/false);
}

export async function runMusic({ projectId, meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_GLOBAL);
  const code = PRICE_CODES.music;
  const units = 1;
  const idem = makeIdem(projectId, 'music', makeRunNonce());

  const worker = () => reserveThen({
    code, units, idem,
    metadata: { projectId, kind: 'music', ...meta },
    run: runCore
  });
  return safeWrap(enabled, worker, false);
}

export async function runTTS({ projectId, text = '', meta = {} }, runCore) {
  const enabled = isEnabled(FEATURE_CREDITS_GLOBAL);
  const code = PRICE_CODES.tts;
  // Estimate: ~15 chars per second for average speech
  const estSeconds = Math.max(1, Math.ceil(text.length / 15));
  const units = estSeconds;
  const idem = makeIdem(projectId, 'tts', makeRunNonce());

  const worker = () => debitAfterSuccess({
    code, units, idem,
    metadata: { projectId, kind: 'tts', textLength: text.length, estSeconds, ...meta },
    run: runCore,
  });
  return safeWrap(enabled, worker, true);
}